import React, { useState } from "react";
import styled from "styled-components";
import {
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalFormGrid,
    StandardModalFormGroup,
    StandardModalInput,
    StandardModalLabel,
    StandardModalSection,
    StandardModalSectionTitle,
    Toast
} from "src/components";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { UserKeys } from "src/constants/userDetails";
import { useContext } from "react";
import { UserContext } from "src/scenes/App/UserContext";
import { setGlobalCustomNames } from "src/utils/helpers";

const StyledTitleInput = styled(StandardModalInput)`
    text-align: left;
    font-size: 1rem;
    width: 364px;
    max-width: 100%;
    height: 50px;
`;

export default function ChangeNameDialog(props) {
    const user = useContext(UserContext);
    const { hideShowNameChangeDialog, customNames, setCustomNames } = props;
    const token = localStorage.getItem(UserKeys.TOKEN);
    const [isLoading, setIsLoading] = useState(false);

    const onChange = (field) => (e) => {
        setCustomNames({ ...customNames, [field]: e.target.value });
    };

    const onSubmit = async () => {
        setIsLoading(true);
        try {
            const defaultEmployees = "Employees";
            const defaultClients = "Clients";

            const newCustomNames = {
                employees: customNames.employees?.trim() || defaultEmployees,
                clients: customNames.clients?.trim() || defaultClients,
            };

            setCustomNames(newCustomNames);

            const response = await axios.put(`${API_URL}/employees/customGroupName`, newCustomNames, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const returnCustomNames = response?.data?.customName;
            setGlobalCustomNames(returnCustomNames.clients, returnCustomNames.employees);

            user?.appFns?.updateUserContextProperties({
                [UserKeys.CUSTOM_NAME_USER_GROUPS_EMPLOYEES]: newCustomNames.employees,
                [UserKeys.CUSTOM_NAME_USER_GROUPS_CLIENTS]: newCustomNames.clients
            });

            hideShowNameChangeDialog();
        } catch (error) {
            console.error(error);
            Toast.error("Failed to save custom names.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <StandardModal
            open={true}
            onClose={hideShowNameChangeDialog}
            title="Change User Naming"
            description="Rename how your users are referred to in the Me Business app and portal to better suit your organisation."
            widthMode="medium"
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton
                        type="button"
                        onClick={hideShowNameChangeDialog}
                        size="0.875rem"
                        width="8.75rem"
                    >
                        Cancel
                    </StandardModalActionButton>
                    <StandardModalActionButton
                        $variant="primary"
                        type="button"
                        onClick={onSubmit}
                        width="8.75rem"
                    >
                        {!isLoading ? "Save" : <LottieLoadingIndicator height="40px" width="40px" style={{ marginTop: "-22px" }}/>}
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <StandardModalSectionTitle>Set a Custom Name (Optional)</StandardModalSectionTitle>
                <StandardModalDescription>
                    Enter a new name to replace the default user label. If left blank, the original label will remain.
                </StandardModalDescription>
                <StandardModalFormGrid columns={1}>
                    <StandardModalFormGroup>
                        <StandardModalLabel>Change &apos;Employees&apos; to</StandardModalLabel>
                        <StyledTitleInput
                            value={customNames.employees}
                            placeholder="Employees"
                            maxLength={30}
                            onChange={onChange("employees")}
                        />
                    </StandardModalFormGroup>
                    <StandardModalFormGroup>
                        <StandardModalLabel>Change &apos;Clients&apos; to</StandardModalLabel>
                        <StyledTitleInput
                            value={customNames.clients}
                            placeholder="Clients"
                            maxLength={30}
                            onChange={onChange("clients")}
                        />
                    </StandardModalFormGroup>
                </StandardModalFormGrid>
            </StandardModalSection>
        </StandardModal>
    );
}
